/**
 * Fetch Consumer Actor
 *
 * Thin wrapper around `matchByPhoneAndDob`. No auto-retry, no fan-out — the
 * machine is responsible for deciding what to do when the backend says
 * "multiple patients matched". This keeps actor responsibilities narrow and
 * ensures every match outcome is visible to the state machine's guards.
 */

import { matchByPhoneAndDob } from "@/app/directories/kiosk/server-actions/consumer-actions";
import type { ConsumerInsurance } from "@/app/directories/kiosk/server-actions/pc-actions";
import { fetchUserEmail } from "@/app/directories/kiosk/server-actions/user-actions";
import { fromPromise } from "xstate";
import { convertBirthdayToISODate } from "./date.utils";

export interface FetchConsumerInput {
  firstName: string;
  lastName: string;
  birthday: string;
  phoneNumber: string;
  orgId: number;
}

/**
 * Unified matched-consumer shape returned by both `fetchConsumerActor` and
 * `disambiguateConsumerActor` on a successful match. Keeping the shape
 * identical lets the machine use a single `assignMatchedConsumer` action on
 * every match transition.
 */
export interface MatchedConsumer {
  id: string;
  userId: string;
  firstname: string;
  lastname: string;
  dob: string;
  email?: string;
  insurances?: ConsumerInsurance[];
}

export interface FetchConsumerOutput {
  message:
    | "CONSUMER_MATCHED"
    | "MULTIPLE_CONSUMERS_MATCHED"
    | "NO_CONSUMER_MATCHED";
  consumer?: MatchedConsumer;
  userPhone?: string;
  correlationId?: string;
  disambiguationField?: string;
}

export const fetchConsumerActor = fromPromise<
  FetchConsumerOutput,
  FetchConsumerInput
>(async ({ input }) => {
  const { birthday, phoneNumber, orgId } = input;

  try {
    const isoDate = convertBirthdayToISODate(birthday);
    if (!isoDate) {
      // Without a parseable birthday we cannot perform a phone+dob match.
      // This happens when the eligibility flow's birthday wasn't persisted
      // (e.g. switching orgs after a previous "passed" eligibility run).
      // Treat it as "no match" so the machine continues to `insurancePayer`
      // — same outcome as a network failure — instead of crashing in
      // `padStart` with an undefined segment.
      console.warn(
        "[fetchConsumerActor] Skipping match — invalid/empty birthday:",
        birthday,
      );
      return { message: "NO_CONSUMER_MATCHED" };
    }
    const normalizedPhone = phoneNumber.replace(/\D/g, "").slice(-10);

    console.log("[fetchConsumerActor] Matching consumer:", {
      phone: normalizedPhone,
      dob: isoDate,
      orgId,
    });

    const response = await matchByPhoneAndDob(normalizedPhone, isoDate, orgId);

    console.log("[fetchConsumerActor] Match response:", {
      message: response.data.message,
      hasConsumer: !!response.data.consumer,
      response: response.data,
    });

    const message = response.data.message;

    if (message === "PATIENT_MATCHED") {
      const consumer = response.data.consumer;

      let userEmail: string | undefined;
      try {
        if (consumer?.userId) {
          const result = await fetchUserEmail(consumer.userId);
          userEmail =
            result.status === "ok" && result.email ? result.email : undefined;
        }
      } catch {
        // Non-critical — proceed without email
      }

      const output: FetchConsumerOutput = {
        message: "CONSUMER_MATCHED",
        consumer: consumer
          ? {
              id: consumer.id,
              userId: consumer.userId,
              firstname: consumer.firstname,
              lastname: consumer.lastname,
              dob: consumer.dob,
              email: userEmail ?? consumer.email ?? undefined,
              insurances: consumer.insurances,
            }
          : undefined,
        userPhone: phoneNumber,
      };
      console.log("[fetchConsumerActor] Returning:", output);
      return output;
    }

    if (message === "MULTIPLE_PATIENTS_MATCHED") {
      const output: FetchConsumerOutput = {
        message: "MULTIPLE_CONSUMERS_MATCHED",
        correlationId: response.data.correlationId,
        disambiguationField: response.data.disambiguationField,
        userPhone: phoneNumber,
      };
      console.log("[fetchConsumerActor] Returning:", output);
      return output;
    }

    const output: FetchConsumerOutput = { message: "NO_CONSUMER_MATCHED" };
    console.log("[fetchConsumerActor] Returning:", output);
    return output;
  } catch (error) {
    console.error("[fetchConsumerActor] Error:", error);
    // Handle 404, 502, and network errors gracefully - treat as no match
    const output: FetchConsumerOutput = { message: "NO_CONSUMER_MATCHED" };
    console.log("[fetchConsumerActor] Returning:", output);
    return output;
  }
});
